import { instanceToPlain } from 'class-transformer';
import { Component } from 'react';
import { cloneDeep } from 'xconvert-web-shared';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { Logger } from '../../models/logger';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { FileManagerService } from '../../services/file-manager.service';
import { FileProcessService } from '../../services/file-process.service';
import { UIContext } from '../../services/ui-context';
import { WebSocketService } from '../../services/websocket.service';
import { ConnectivityUtils } from '../../utils/connectivity.utils';
import { gifEstimateStore } from '../../utils/gif-estimate.store';
import { PageData } from '../../types/route-types';
import { OrientationUpdateEvent, ProcessTypes } from '../../types/types';
import { FileUploadUtils } from '../../utils/file-upoad.utils';
import { scheduleCoordinator } from '../../services/scheduling/schedule-coordinator';
import { isPerFileMergeType } from '../../utils/merge-type.utils';
import { ConvertOptionsContextV2 } from '../convert-options-v2/convert-options.context';
import FileCardComponent, { UpdateProcessProbedDataFnProvider } from '../ui/file-card/file-card.component';
import SortableComponent from '../ui/sortable/sortable.component';

interface FileProcessEditorProps {
    initialFiles?: UploadInProgressFile<any>[];
    convertOptionsContext: ConvertOptionsContextV2;
    fromType?: string[];
    toType: string;
    processType: string;
    pageData: PageData;
    onFilesChange?: (files: UploadInProgressFile<any>[]) => void;
    onConversionSuccess?: () => void;
    probeDataProvider?: UpdateProcessProbedDataFnProvider;
    hideRemoveButton?: boolean;
    children?: React.ReactNode;
}

interface FileProcessEditorState {
    files: UploadInProgressFile<any>[];
}

export class FileProcessEditorComponent extends Component<FileProcessEditorProps, FileProcessEditorState> {
    private readonly logger = Logger.getLogger('FileProcessEditorComponent');

    @lazyInject(FileProcessService) fileProcessService: FileProcessService;
    @lazyInject(WebSocketService) websocketService: WebSocketService;
    @lazyInject(UIContext) uiContext: UIContext;
    @lazyInject(FileManagerService) fileManagerService: FileManagerService;

    constructor(props: FileProcessEditorProps) {
        super(props);
        this.state = {
            files: props.initialFiles || []
        };
    }

    componentDidUpdate(prevProps: FileProcessEditorProps, prevState: FileProcessEditorState) {
        if (this.state.files !== prevState.files) {
            // The editor owns the file list, so it owns estimate-record lifecycle:
            // any entry that left the list (remove button, wholesale updateFiles)
            // drops its record and marks in-flight probes stale. Sorting keeps the
            // same entries, so nothing is dropped there.
            const currentIds = new Set(this.state.files.filter(f => f).map(f => f.id));
            prevState.files.filter(f => f && !currentIds.has(f.id))
                .forEach(f => gifEstimateStore.remove(f.id));
            if (this.props.onFilesChange) {
                this.props.onFilesChange(this.state.files);
            }
        }
    }

    componentWillUnmount() {
        // Page navigation / editor teardown: cancel every tracked estimate
        // (binding lifecycle guard — no leaked promises or timers).
        this.state.files.filter(f => f).forEach(f => gifEstimateStore.remove(f.id));
    }

    public addFiles(newFiles: UploadInProgressFile<any>[]) {
        this.setState(prevState => ({
            files: [...prevState.files, ...newFiles]
        }));
    }

    public updateFiles(files: UploadInProgressFile<any>[]) {
        this.setState({ files });
    }

    public getFiles() {
        return this.state.files;
    }

    removeFile(file: any) {
        this.setState(prevState => {
            const newFiles = prevState.files.filter(f => f.getFile() !== file);
            return { files: newFiles };
        });
    }

    orientationChanged(orientationData: OrientationUpdateEvent) {
        const uploadInProgressFile = this.state.files.find(fm => fm && fm.getFile() === orientationData.file);
        if (!uploadInProgressFile) {
            this.logger.trace1('Set orientation was called on a file that is not on uploadInProgressFiles list');
            return;
        }
        uploadInProgressFile.setOrientation(orientationData.orientation);
        this.forceUpdate();
    }

    sortOrderChanged(e: any) {
        // Optional: Notify parent if needed, but state update happens in sortEnded usually or here if real-time
    }

    sortEnded(e: any) {
        this.setState({ files: e });
    }

    prepareMergeOptionsAndSnapshot() {
        const summary = instanceToPlain(this.props.convertOptionsContext.getSummary());
        const summaryJson = JSON.parse(JSON.stringify(summary));
        const optionsSnapshot = cloneDeep(summaryJson);

        const orientationAngleList: string[] =
            this.state.files.map(fm => (fm.getOrientation() && fm.getOrientation().angle) || '0');
        const orientationFlipList: string[] =
            this.state.files.map(fm => (fm.getOrientation() && fm.getOrientation().isFliped) || 'false');
        summaryJson.orientationAngleList = orientationAngleList;
        summaryJson.orientationFlipList = orientationFlipList;
        return { options: summaryJson, optionsSnapshot };
    }

    /**
     * Determines if files should be merged into a single output.
     * Checks both processType and mergeType options.
     */
    private shouldMergeFiles(): boolean {
        const summary = this.props.convertOptionsContext.getSummary() as any;
        const mergeType = summary?.mergeType;
        if (isPerFileMergeType(mergeType)) return false;
        if (this.props.processType === ProcessTypes.MERGE || this.props.processType === ProcessTypes.ARCHIVE) {
            return true;
        }
        return mergeType === 'MERGE_PDF' || mergeType === 'MERGE_ARCHIVE' || mergeType === 'MERGE_VIDEO' || mergeType === 'MERGE_ANIMATED';
    }

    /**
     * Set while one batch's cards are being dispatched, so the queued/stalled
     * outcome produces AT MOST ONE toast for the whole batch rather than one
     * modal per file (plan §4.4: "Batch header row, one toast per batch, at most
     * one modal"). During the 2026-09-05 incident a 340-file batch would have
     * opened 340 "Connection Issue" dialogs on top of each other.
     */
    private batchQueuedNoticeShown = false;

    public async startConversion() {
        const processPromises: Promise<void>[] = [];
        this.batchQueuedNoticeShown = false;

        if (this.shouldMergeFiles()) {
            const { options, optionsSnapshot } = this.prepareMergeOptionsAndSnapshot();
            const fileUpload = FileUploadUtils.createMergeFileUpload(
                this.state.files,
                this.websocketService.getId(),
                this.props.processType as any,
                this.props.fromType as any,
                this.props.toType as any,
                options,
                optionsSnapshot,
                this.props.pageData
            );

            this.fileManagerService.addFileUpload(fileUpload);
            processPromises.push(this.processFileWithFeedback(fileUpload));
        } else {
            const fileUploads = FileUploadUtils.createSingleFileUploads(
                this.state.files,
                this.websocketService.getId(),
                this.props.processType as any,
                this.props.fromType as any,
                this.props.toType as any,
                this.props.convertOptionsContext.getSummary(),
                this.props.pageData
            );

            fileUploads.forEach(fileUpload => {
                this.fileManagerService.addFileUpload(fileUpload);
                processPromises.push(this.processFileWithFeedback(fileUpload));
            });
        }

        await Promise.all(processPromises);

        if (this.props.onConversionSuccess) {
            this.props.onConversionSuccess();
        }
    }


    private processFileWithFeedback(fileUpload: FileUpload): Promise<void> {
        return new Promise((resolve, reject) => {
            this.uiContext.successToast('Starting conversion...', 'Connecting to server', 3000);
            this.fileProcessService.processFile(fileUpload).subscribe({
                next: (data) => {
                    // The hands-off path emits a `{queued:true}` sentinel as soon
                    // as the card has a state worth showing: the schedule call has
                    // NOT succeeded, so there is nothing to retrack — the card is
                    // already on the list and the coordinator owns it from here.
                    if ((data as any)?.queued === true) {
                        this.noteBatchQueued((data as any)?.stalled === true);
                        resolve();
                        return;
                    }
                    this.fileManagerService.retrackFileUpload(fileUpload);
                    this.logger.trace7(data);
                    resolve();
                },
                error: (err) => {
                    this.logger.error({ message: 'File process failed.', error: err, data: FileUpload.toLoggableObject(fileUpload) });
                    if (err?.isEnforcementError) {
                        // Keep file on card — don't remove. Set enforcement error for display.
                        fileUpload.result = { error: true, dataType: 'enforcement_error', data: JSON.stringify(err.enforcementData) };
                        fileUpload.ws = { processStage: 'COMPLETED', processStageSubState: 'FAILED', dataType: 'enforcement_error', data: err.enforcementData?.userMessage || 'Usage limit reached.' } as any;
                        this.fileManagerService.saveFileUploadToStorage(fileUpload);
                        resolve();
                        return;
                    }
                    // THE INVERTED ERROR BRANCH (plan §4.1, correction 16;
                    // clientQueuedState only). 2026-09-05: `removeFile` used to run
                    // HERE, before either dialog — and it fires
                    // `GET /file-manager/delete/<id>` on the upload, process AND
                    // download domains, so the uploaded bytes were destroyed before
                    // the "Connection Issue" dialog offered a Retry that could no
                    // longer work. Under the flag the ONLY failure that still
                    // deletes bytes is a deterministic 4xx (`scheduleTerminal`):
                    // a 5xx, a 409, a timeout and an offline browser all keep the
                    // card AND the file, and the card says so.
                    if (scheduleCoordinator.enabled && !err?.scheduleTerminal) {
                        this.noteBatchQueued(true);
                        resolve();
                        return;
                    }
                    this.fileManagerService.removeFile(fileUpload);
                    if (ConnectivityUtils.isConnectivityError(err) || ConnectivityUtils.isRetryableUploadError(err)
                        || err?.message?.includes('All requests failed')) {
                        this.uiContext.showErrorDialog({
                            title: 'Connection Issue',
                            text: 'Could not reach the processing server. This is usually caused by a network issue. Please check your connection and try again.',
                            confirmButtonText: 'Retry',
                            successCallback: () => {
                                this.fileManagerService.addFileUpload(fileUpload);
                                this.processFileWithFeedback(fileUpload).then(resolve).catch(reject);
                            },
                            showCancelButton: true,
                            cancelButtonText: 'Cancel',
                            cancelCallback: () => reject(err),
                        });
                    } else {
                        this.uiContext.showErrorDialog({
                            title: 'Error',
                            text: `Failed to ${this.props.processType} the file. ${err?.message || 'Please try again.'}`,
                        });
                        reject(err);
                    }
                }
            });
        });
    }

    /**
     * At most ONE toast for the whole batch (plan §4.4). No per-file modal: the
     * card itself says "Queued — server busy, retrying" / "Not started — your
     * file is still here", and the batch header on /downloads carries the single
     * "Retry all". Provenance: 2026-09-05 incident, reviewer finding SaaS #5.
     */
    private noteBatchQueued(stalled: boolean) {
        if (this.batchQueuedNoticeShown) return;
        this.batchQueuedNoticeShown = true;
        if (stalled) {
            this.uiContext.warnToast(
                'Not started yet',
                'The server is busy. Your files are safe — use "Retry all" on the downloads page when you are ready.',
                false, false,
            );
            return;
        }
        this.uiContext.successToast(
            'Queued',
            'The server is busy right now. We keep retrying — your files stay where they are.',
            6000,
        );
    }

    render() {
        return (
            <div className={`container-fluid g-0`}>
                <SortableComponent
                    items={this.state.files}
                    sortHandlerClass='sort-handler'
                    classes={['row g-0']}
                    orderChanged={(e) => this.sortOrderChanged(e)}
                    sortEnded={(e) => this.sortEnded(e)}>
                    {
                        this.state.files.filter(f => f).map((uploadInProgressFile, idx) => {
                            return <div key={`${uploadInProgressFile.uuid}-parent-${idx}`}
                                data-notsortable={false}
                                data-uniquekey={uploadInProgressFile.uuid}
                                className={`sortable ${uploadInProgressFile.file.type.indexOf('image') === -1 ? 'non-image' : 'image'} col-12`}
                            >
                                <FileCardComponent
                                    uploadInProgressFile={uploadInProgressFile}
                                    convertOptionsContext={this.props.convertOptionsContext}
                                    convertOptionCategories={this.props.convertOptionsContext} // Passing context as categories as per original usage
                                    updateProcessProbedDataFnProvider={this.props.probeDataProvider}
                                    toType={this.props.toType as any}
                                    fromType={this.props.fromType as any}
                                    processType={this.props.processType}
                                    removeFile={$event => this.removeFile($event)}
                                    hideRemoveButton={this.props.hideRemoveButton}
                                    orientationChanged={$event => this.orientationChanged($event)}>
                                </FileCardComponent>
                            </div>
                        })
                    }
                </SortableComponent>
                {this.props.children}
            </div>
        );
    }
}
